import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  NgModule,
  NgZone,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { AngularSplitModule } from 'angular-split';
import { FileSaverModule, FileSaverService } from 'ngx-filesaver';
import { BlocklyModule } from 'src/app/shared/components/blockly/blockly.module';
import { ToastrService } from 'ngx-toastr';
import { IDEService } from 'src/app/shared/services/ide.service';
import { WsApplicationService } from 'src/app/shared/services/ws-application.service';
import { Store } from '@ngrx/store';
import { IAppState } from 'src/app/store/index.state';
import { showSidePanel } from 'src/app/store/layout/layout.action';
import { sidePanelSelector } from 'src/app/store/layout/layout.selector';
import { Subject, Subscription } from 'rxjs';
import { SidePanelModule } from 'src/app/components/side-panel/side-panel.module';
import { SessionStorageService } from 'src/app/shared/services/session-storage.service';
import { BlocklyComponent } from 'src/app/shared/components/blockly/blockly.component';
import { PythonDirectModule } from '../../container/python-direct/python-direct.module';
import { BlockComparisonResult } from 'src/app/shared/models/block';
import { DevicesService } from 'src/app/shared/services/devices.service';
import { SupportedCodeLang } from 'src/app/shared/domain/code-gen/code-lang.types';
import { BlocklyConfigService } from 'src/app/shared/services/blockly-config.service';
import { GeneratedCodeService } from 'src/app/shared/services/generated-code.service';
import { DownloadOptionsService } from 'src/app/shared/services/download-options.service';
import { filter, take, takeUntil } from 'rxjs/operators';
import { isSupportedCodeLang } from 'src/app/shared/domain/code-gen/code-lang.utils';

@Component({
  selector: 'app-blockly-section',
  templateUrl: './blockly-section.component.html',
  styleUrls: ['./blockly-section.component.scss'],
})
export class BlocklySectionComponent
  implements OnInit, OnChanges, AfterViewInit {
  @Input() codeLang!: SupportedCodeLang;
  @Input() ieeeEvent: any;
  @Input() nielitEvent: boolean = false;
  @Input() applicationIdentifier!: string;
  @Input() selectedApplication: any;
  @Input() switchType: any = 'block';
  @Input() code: any;
  @Input() toggle: any;
  @Input() isAdmin: boolean = false;
  @Input() moduleKey!: string;
  @Input() parentComponent: string = BlocklySectionComponent.name;
  @Output() emitCodeEvent = new EventEmitter();
  @Output() emitXMLEvent = new EventEmitter();
  @Output() emitBlockState = new EventEmitter();
  @Output() switchTypeChange = new EventEmitter();

  // Block comparison variables
  isCompareRequested: boolean = false;
  @Input() isCompareResult: BlockComparisonResult =
    BlockComparisonResult.Default;
  @Output() rightBlockCodeEmitter = new EventEmitter();
  @Output() compareBlockEmitter = new EventEmitter();
  @Output() resetCompareBlockResultEmitter = new EventEmitter();

  @Output() getGeneratedCode = new EventEmitter();

  readonly downloadOptions$ = this.blocklyConfigService.downloadOptions$;
  readonly generatedCode$ = this.generatedCodeService.generatedCode$;
  readonly codeLines$ = this.generatedCodeService.codeLines$;
  readonly isCodePresent$ = this.generatedCodeService.isCodePresent$;

  showSidePanel$ = this.store?.select(sidePanelSelector);
  isTemplate: boolean = window.location.href.includes('template');

  wsSubscription!: Subscription;
  toolbox: string = '';
  size: any;
  showSelectOptions: boolean = false;
  isCodePresent: boolean = false;
  buttonActive: string = 'opacity-100';
  buttonInActive: string = 'opacity-50';
  isDirect = false;
  isRPIConnected = false; // is RPI Registered
  isBlockWithSimulator: boolean = false;
  isDeviceOnline!: boolean;
  hostName: string = '';
  downloadTo: string = '';
  level: any = 'beginner';
  isBlock: boolean = false;
  current_username: string;
  isCompiled: boolean = false;
  public deviceId!: string;
  private statusCollected = false;
  private lastStoreTimeStamp!: number;
  associatedDevices: any;
  getAssociatedDevice: any;
  isDeviceConnected: boolean = false;

  @ViewChild(BlocklyComponent)
  blocklyComponent!: BlocklyComponent;

  private destroy$ = new Subject<void>();

  constructor(
    private fileSaverService: FileSaverService,
    private toasterService: ToastrService,
    private ideService: IDEService,
    private store: Store<IAppState>,
    private wsService: WsApplicationService,
    private sessionStorageService: SessionStorageService,
    private devicesService: DevicesService,
    private blocklyConfigService: BlocklyConfigService,
    private generatedCodeService: GeneratedCodeService,
    private downloadOptionsService: DownloadOptionsService,
    private ngZone: NgZone
  ) {
    this.current_username = this.sessionStorageService.getUsername();
    this.isBlock = (
      window.location.href.includes('/ide/block') ||
      window.location.href.includes('/ide/application/block') ||
      window.location.href.includes('/ide/template/block')
    );
  }

  get currentCode(): string {
    return this.generatedCodeService.currentCode;
  }

  async ngOnInit(): Promise<void> {
    this.codeLang = "python";
    this.isDirect = window.location.href.includes('block-direct');
    this.level = sessionStorage.getItem('module_level');

    if (!this.validateCodeLang()) return;

    this.setupBlocklyConfig();
  }

  ngAfterViewInit(): void {
    // console.log("bockly component ",this.blocklyComponent)
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    const { switchType, moduleKey, codeLang, ieeeEvent, nielitEvent, isCompareResult } = changes;

    if (switchType?.currentValue) {
      await this.handleSwitchTypeChange(switchType.currentValue);
    }

    if (codeLang && !codeLang.firstChange && isSupportedCodeLang(codeLang.currentValue)) {
      this.generatedCodeService.clearCode();
      this.toolbox = '';
      this.blocklyConfigService.updateConfig({ codeLang: codeLang.currentValue });
    }

    if (moduleKey?.currentValue) {
      this.toolbox = '';
      this.blocklyConfigService.updateConfig({ moduleKey: moduleKey.currentValue });
      await this.getAssociatedDevices();
    }

    if (ieeeEvent || nielitEvent) {
      this.blocklyConfigService.updateConfig({
        hideGatewayOption: this.resolveHideGatewayOption()
      });
    }

    if (this.isCompareRequested && isCompareResult?.currentValue) {
      await this.verifyAndDownloadPart2();
      this.resetCompareBlockResultEmitter.emit({});
    }
  }

  private async handleSwitchTypeChange(switchType: string): Promise<void> {
    if (switchType === 'CODE') {
      this.isCompiled = true;
      if (!this.statusCollected && !this.isAdmin) {
        await this.getConnectedDevice();
      }
    } else if (switchType.toLowerCase() === 'block') {
      this.isCompiled = false;
      this.deferWorkspaceResize();
    }
  }

  private deferWorkspaceResize(): void {
    this.ngZone.onStable.pipe(take(1)).subscribe(() => {
      requestAnimationFrame(() => this.resizeBlocklyWorkspace());
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private validateCodeLang(): boolean {
    if (!this.codeLang || !isSupportedCodeLang(this.codeLang)) {
      this.toasterService.error('Code Language not specified or unsupported');
      return false;
    }
    return true;
  }

  private setupBlocklyConfig(): void {
    this.blocklyConfigService.toolbox$
      .pipe(takeUntil(this.destroy$))
      .subscribe(toolbox => {
        this.toolbox = toolbox;
      });

    this.blocklyConfigService.updateConfig({
      codeLang: this.codeLang,
      moduleKey: this.moduleKey || '',
      hideGatewayOption: this.resolveHideGatewayOption(),
      isAdmin: this.isAdmin,
      isDirect: this.isDirect
    });
  }

  private resizeBlocklyWorkspace(): void {
    const nativeWs = this.blocklyComponent?.workspace?.workspace;
    if (!nativeWs) {
      return;
    }

    try {
      nativeWs.resize();

      if (typeof Blockly.svgResize === 'function') {
        Blockly.svgResize(nativeWs);
      }

      nativeWs.render();
    } catch (error) {
    }
  }

  private resolveHideGatewayOption(): boolean {
    return this.ieeeEvent || window.location.href.includes('nielit');
  }

  async getAssociatedDevices() {
    if (!window.location.href.includes('nielit')) {
      try {
        const result: any = await this.devicesService.getUserAssociateDevices(this.current_username).toPromise();
        this.associatedDevices = result.devices;
      } catch (err: any) {
        this.toasterService.error(err.error.message);
      }
    }
  }

  onCode(code: string): void {
    this.generatedCodeService.setCode(code);

    if (code) {
      this.getGeneratedCode.emit(code.split('\n'));
    } else {
      this.getGeneratedCode.emit([]);
      this.showOptions(false);
    }

    this.emitRightBlock();
  }

  toggleBlockState(state: boolean) {
    this.isBlockWithSimulator = state;
    this.emitBlockState.emit(this.isBlockWithSimulator);
  }

  sidePanelClosed(event: any) {
    if (event) {
      this.store.dispatch(showSidePanel({ value: false }));
      this.selectedApplication = null;
    }
  }

  async getRPIStatus() {
    if (!this.isAdmin && ['data_science', 'artificial_intelligence'].indexOf(this.moduleKey) === -1) {
      this.ideService
        .getRPIStatus(this.current_username)
        .then((result: any) => {
          this.isRPIConnected = !!result?.result?.length;
          this.hostName = result.result[0].hostname;
          this.deviceId = result.result[0].deviceIdentifier;
          this.createWebSocketConnection();
        })
        .catch((err) => console.log(err));
    }
  }

  async createWebSocketConnection() {
    const appId = this.deviceId;
    if (appId && !this.isAdmin) {
      this.wsSubscription = this.wsService
        .createObservableCircuit(appId)
        .subscribe(async (data: any) => {
          let parsedData = JSON.parse(data);
          if (parsedData) {
            if (parsedData && parsedData.timestamp) {
                const rawTs = Number(parsedData.timestamp) || 0;
                const normalizedMs = rawTs === 0 ? 0 : (rawTs > 1e12 ? rawTs : rawTs * 1000);
                if (!this.lastStoreTimeStamp || (normalizedMs - this.lastStoreTimeStamp) > 5000) {
                  this.lastStoreTimeStamp = normalizedMs;
                }
              }
          }
        });
    }
    this.isDeviceConnected = true;
  }

  async getConnectedDevice() {
    try {
      const result: any = await this.ideService
        .getDeviceStatus(this.deviceId)
        .toPromise();
      if (result) {
        this.statusCollected = true;
        setInterval(() => {
          this.isDeviceOnline =
            (new Date().getTime() - this.lastStoreTimeStamp) / 1000 < 25;
        }, 1000);
      }
    } catch (error) {}
  }

  /**
   * @deprecated The method should not be used
   */
  convertCodeToDiv() {
    return this.currentCode.split('\n');
  }

  onDrag(event: any) {
    this.size = event.sizes;
  }

  showOptions(data: boolean) {
    this.showSelectOptions = data;
  }

   async onChangeAssociateDevice(event : Event){
    const selectElement = event.target as HTMLSelectElement;
    const selectedIndex = parseInt(selectElement.value);
    if (!isNaN(selectedIndex)) {
      const selectedDevice = this.associatedDevices[selectedIndex];
    this.getAssociatedDevice = selectedDevice;
    this.hostName = selectedDevice.hostname;
    this.deviceId = selectedDevice.deviceidentifier;
    if(this.getAssociatedDevice.deviceidentifier != ""){
      await this.getConnectedDevice();
      await this.createWebSocketConnection();
      this.isRPIConnected = true;
    }else {
      this.isRPIConnected = false;
    }
  }else {
    this.isRPIConnected = false;
    this.getAssociatedDevice = "";
  }
}

  close() {
    this.wsService.close();
  }

  private isCodeVerified(userCode: any): boolean {
    if (this.isAdmin || this.isDirect) {
      return true;
    }

    const code = JSON.stringify(this.code);
    if (userCode === null || undefined || '') {
      return false;
    }
    if (userCode === code) {
      return true;
    }

    return false;
  }

  async verifyAndDownloadPart1(downloadTo: any) {
    if(this.getAssociatedDevice === '' && this.switchType.toLowerCase() === "code"){
      this.toasterService.error('Kindly select the Associated Device!');
      this.showSelectOptions = false;
    }
    else {
      this.downloadTo = downloadTo;
      this.isCompareRequested = true;
      // Initiate request to compare blocks before downloading
      await this.emitCompareBlock();

      if (!this.isBlock) {
        this.isCompareResult = BlockComparisonResult.Success;
        this.verifyAndDownloadPart2();
      }
    }
  }

  async verifyAndDownloadPart2() {
    // const userCode = JSON.stringify(this.generatedCode);
    if (this.isCompareResult === BlockComparisonResult.Error) {
      if (
        this.level === 'beginner' ||
        this.level == null ||
        this.level == 'null'
      ) {
        this.cancelDownload();
      } else {
        this.store.dispatch(showSidePanel({ value: true }));
      }
    } else if (this.isCompareResult === BlockComparisonResult.Success) {
      this.downloadCode();
    }
    this.showOptions(false);
    this.isCompareResult = BlockComparisonResult.Default;
    this.isCompareRequested = false;
  }

  downloadCode() {
    if (this.downloadOptionsService.isGatewayTarget(this.downloadTo)) {
      this.downloadToRaspberry();
    } else {
      this.downloadGeneratedCode();
    }
    this.switchType = 'BLOCK';
    this.switchTypeChange.emit(this.switchType);
    window.dispatchEvent(new Event('resize'));
  }

  cancelDownload() {
    const userCode = JSON.stringify(this.currentCode);
    if (this.downloadTo === 'raspberry-pi' && (!this.isDeviceOnline || !this.isRPIConnected)) {
      if(!this.isRPIConnected) {
        this.toasterService.error('Raspberry Pi is not registered');
      } else if(!this.isDeviceOnline) {
        this.toasterService.error('Raspberry Pi is offline');
      }
    } else if (!this.isCodeVerified(userCode)) {
      this.toasterService.error("Code does not match");
      this.switchType = "BLOCK";
      this.switchTypeChange.emit(this.switchType);
      window.dispatchEvent(new Event("resize"));
    }
    this.switchType = 'BLOCK';
    this.switchTypeChange.emit(this.switchType);
    window.dispatchEvent(new Event('resize'));
    this.store.dispatch(showSidePanel({ value: false }));
  }

  private downloadGeneratedCode(): void {
    if (!this.validateCodeLang()) {
      return;
    }

    const extension = this.blocklyConfigService.getFileExtension();
    const filename = `code${extension}`;

    this.toasterService.success('Code is Downloaded');
    this.fileSaverService.saveText(this.currentCode, filename);
    this.store.dispatch(showSidePanel({ value: false }));
  }

  private async downloadToRaspberry() {
    if (!this.isDeviceOnline) {
      this.toasterService.error('Raspberry Pi is offline');
      this.store.dispatch(showSidePanel({ value: false }));
      return;
    }
    try {
      const blob = new Blob([this.currentCode], {
        type: 'text/x-python-script',
      });
      const file = new File([blob], 'code.py', {
        type: 'text/x-python-script',
      });
      let result = await this.initiateUpload(file);
      if (result) {
        const appName = result['applicationName'];
        let toast_msg = `The code is downloaded. \n
        Location: /Desktop/Grok-Downloads/${appName}/code.py`;
        this.toasterService.success(toast_msg);
      } else {
        let toast_msg = `Download failed`;
        this.toasterService.error(toast_msg);
      }
    } catch (error) {
      // Nothing to do here...
    } finally {
      this.store.dispatch(showSidePanel({ value: false }));
    }
  }

  private async initiateUpload(file: File) {
    try {
      const initiationResult: any = await this.ideService
        .initiateUpload(this.deviceId, file, this.current_username)
        .toPromise();
      const link = initiationResult['link']; // S3 URL to upload
      const key = initiationResult['key']; // Filename
      await this.ideService.uploadToS3(file, link).toPromise();

      const appId: any =
        sessionStorage.getItem('app_id') ||
        this?.selectedApplication?.application_identifier ||
        new Date().getTime();
      let response: any = await this.ideService
        .generateCodeDownloadLink(this.deviceId, appId, key, this.current_username)
        .toPromise();
      return response;
    } catch (error: any) {
      this.toasterService.error(error.error.message);
    }
  }

  undo() {
    // this.workspace.undo();
    // this.buttonClicked.emit();
  }
  isScreenCollaps(event: any) {
    this.isCollasped = event;
    // this.isCollasped=!this.isCollasped;
  }

  isCollasped: boolean = true;
  onCollasped() {
    this.isCollasped = !this.isCollasped;
  }

  async emitRightBlock(event?: any) {
    let rightBlock =
      this.blocklyComponent.workspace.workspace.getAllBlocks(true);
    await this.rightBlockCodeEmitter.emit({ rightBlock });
  }

  async emitCompareBlock(event?: any) {
    await this.compareBlockEmitter.emit({});
  }

  retryConnection() {
    this.createWebSocketConnection();
  }
}

@NgModule({
  declarations: [BlocklySectionComponent],
  imports: [
    CommonModule,
    BlocklyModule,
    AngularSplitModule,
    SidePanelModule,
    FileSaverModule,
    PythonDirectModule,
  ],
  exports: [BlocklySectionComponent],
})
export class BlocklySectionModule {}